import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CalendarDays,
  RefreshCw,
  AlertTriangle,
  Activity,
  Trash2,
  Plus,
  Loader2,
  ShieldAlert,
  Globe,
} from 'lucide-react';
import { holidayAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  CoverageAndMetricsPanel,
  BulkAndPacksPanel,
  UpcomingImpactPanel,
  PHAuditTrailPanel,
  FeatureFlagsPanel,
} from '../components/admin/HolidaysPhase3Tabs';

const SOURCE_BADGES = {
  nager: { label: 'System', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  manual: { label: 'Custom', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  override: { label: 'Override', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  ph_override: { label: 'PH Override', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const PH_TYPES = [
  { value: 'regular', label: 'Regular Holiday (2.0×)' },
  { value: 'special_non_working', label: 'Special Non-Working (1.3×)' },
  { value: 'special_working', label: 'Special Working (1.0×)' },
];

function SourceBadge({ source }) {
  const s = SOURCE_BADGES[source] || { label: source || '—', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <span
      data-testid={`holiday-source-badge-${source || 'unknown'}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

function StatusDot({ status }) {
  const map = {
    success: 'bg-emerald-500',
    empty: 'bg-slate-400',
    failed: 'bg-rose-500',
    running: 'bg-sky-500 animate-pulse',
  };
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${map[status] || 'bg-slate-300'}`} />;
}

const COUNTRIES = ['CA', 'US', 'GB', 'AU', 'NZ', 'FR', 'PH'];

export default function AdminHolidays() {
  const { user } = useAuth();
  const isPHTenant =
    (user?.country_code || '').toUpperCase() === 'PH' ||
    (user?.country || '').toLowerCase().includes('philippin');

  // ── filters ────────────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [countryFilter, setCountryFilter] = useState('CA');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showDisabled, setShowDisabled] = useState(true);

  // ── data ───────────────────────────────────────────────────────────────
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── add custom holiday modal ───────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    holiday_name: '',
    holiday_date: '',
    country_code: 'CA',
    province_state_code: '',
    is_paid: true,
  });
  const [adding, setAdding] = useState(false);

  // ── ph override modal ─────────────────────────────────────────────────
  const [showPHModal, setShowPHModal] = useState(false);
  const [phForm, setPHForm] = useState({
    holiday_name: '',
    holiday_date: '',
    ph_type: 'special_non_working',
    is_paid: true,
    note: '',
    reason: '',
  });
  const [submittingPH, setSubmittingPH] = useState(false);

  // ── sync diagnostics ───────────────────────────────────────────────────
  const [diagnostics, setDiagnostics] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [running, setRunning] = useState(false);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        country_code: countryFilter,
        year: yearFilter,
        include_disabled: showDisabled,
      };
      if (provinceFilter) params.province_state_code = provinceFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      const r = await holidayAPI.list(params);
      setHolidays(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [countryFilter, provinceFilter, yearFilter, sourceFilter, showDisabled]);

  const fetchDiagnostics = useCallback(async () => {
    try {
      const [d, l] = await Promise.all([
        holidayAPI.syncDiagnostics(),
        holidayAPI.syncLogs({ limit: 20 }),
      ]);
      setDiagnostics(d.data);
      setSyncLogs(l.data.items || []);
    } catch (e) {
      // silent — diagnostics is non-critical
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  useEffect(() => {
    fetchDiagnostics();
    const t = setInterval(fetchDiagnostics, 15000);
    return () => clearInterval(t);
  }, [fetchDiagnostics]);

  // ── toggle disable / delete ───────────────────────────────────────────
  const toggleDisabled = async (h) => {
    try {
      await holidayAPI.patch(h.id, { is_disabled: !h.is_disabled });
      toast.success(h.is_disabled ? 'Holiday re-enabled' : 'Holiday disabled');
      fetchHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete custom holiday "${h.holiday_name}"? This cannot be undone.`)) return;
    try {
      await holidayAPI.remove(h.id);
      toast.success('Custom holiday deleted');
      fetchHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── add custom holiday submit ─────────────────────────────────────────
  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.holiday_name.trim() || !addForm.holiday_date) {
      toast.error('Name and date are required');
      return;
    }
    setAdding(true);
    try {
      await holidayAPI.create({
        ...addForm,
        country_code: addForm.country_code.toUpperCase(),
        province_state_code: addForm.province_state_code?.toUpperCase() || null,
      });
      toast.success('Custom holiday added');
      setShowAddModal(false);
      setAddForm({ holiday_name: '', holiday_date: '', country_code: countryFilter, province_state_code: '', is_paid: true });
      fetchHolidays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setAdding(false);
    }
  };

  // ── ph override submit ────────────────────────────────────────────────
  const submitPH = async (e) => {
    e.preventDefault();
    if (!phForm.holiday_name.trim() || !phForm.holiday_date) {
      toast.error('Name and date are required');
      return;
    }
    if ((phForm.reason || '').trim().length < 10) {
      toast.error('Reason must be at least 10 characters (compliance audit)');
      return;
    }
    setSubmittingPH(true);
    try {
      await holidayAPI.phOverride(phForm);
      toast.success('PH emergency override saved');
      setShowPHModal(false);
      setPHForm({ holiday_name: '', holiday_date: '', ph_type: 'special_non_working', is_paid: true, note: '', reason: '' });
      fetchHolidays();
      fetchDiagnostics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSubmittingPH(false);
    }
  };

  // ── manual sync (debounced via diagnostics + button lock) ─────────────
  const handleRunSyncNow = async () => {
    if (running || diagnostics?.currently_syncing) {
      toast.info('A sync is already running. Please wait.');
      return;
    }
    setRunning(true);
    try {
      const r = await holidayAPI.syncRunNow();
      toast.success(`Sync completed: ${r.data.rows} rows across ${r.data.runs?.length || 0} runs`);
      fetchHolidays();
      fetchDiagnostics();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info('Sync already in progress.');
      } else {
        toast.error(e?.response?.data?.detail || 'Sync failed');
      }
    } finally {
      setRunning(false);
    }
  };

  const handleResyncCountry = async (cc) => {
    try {
      await holidayAPI.syncCountry(cc, yearFilter);
      toast.success(`${cc} ${yearFilter} resynced`);
      fetchHolidays();
      fetchDiagnostics();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info('Sync already in progress.');
      } else {
        toast.error(e?.response?.data?.detail || `${cc} resync failed`);
      }
    }
  };

  const yearOptions = useMemo(() => {
    const ys = [];
    for (let y = currentYear - 1; y <= currentYear + 2; y += 1) ys.push(y);
    return ys;
  }, [currentYear]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-holidays-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-emerald-600" />
              Holidays
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage system holidays, custom dates, and sync diagnostics. Strictly additive — no payroll changes yet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunSyncNow}
              disabled={running || diagnostics?.currently_syncing}
              data-testid="holidays-sync-now-btn"
            >
              {running || diagnostics?.currently_syncing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1.5" />
              )}
              {diagnostics?.currently_syncing ? 'Syncing…' : 'Run Sync Now'}
            </Button>
            <Button size="sm" onClick={() => setShowAddModal(true)} data-testid="holidays-add-custom-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Custom
            </Button>
          </div>
        </div>

        <Tabs defaultValue="holidays" className="w-full">
          <TabsList>
            <TabsTrigger value="holidays" data-testid="tab-holidays">Holidays</TabsTrigger>
            <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">
              <Activity className="h-3.5 w-3.5 mr-1.5" />
              Sync Diagnostics
            </TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk & Packs</TabsTrigger>
            <TabsTrigger value="impact" data-testid="tab-impact">Impact</TabsTrigger>
            <TabsTrigger value="flags" data-testid="tab-flags">Flags</TabsTrigger>
            {isPHTenant && (
              <TabsTrigger value="ph" data-testid="tab-ph-overrides">
                <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                PH Overrides
              </TabsTrigger>
            )}
          </TabsList>

          {/* HOLIDAYS TAB */}
          <TabsContent value="holidays" className="space-y-4 mt-4">
            {/* Filters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 rounded-lg border bg-slate-50/50">
              <div>
                <Label className="text-xs">Country</Label>
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Province/State</Label>
                <Input
                  placeholder="e.g. ON, CA-ON"
                  value={provinceFilter}
                  onChange={(e) => setProvinceFilter(e.target.value)}
                  data-testid="filter-province"
                />
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Select value={String(yearFilter)} onValueChange={(v) => setYearFilter(Number(v))}>
                  <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="filter-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="nager">System (Nager)</SelectItem>
                    <SelectItem value="manual">Custom</SelectItem>
                    <SelectItem value="override">Override</SelectItem>
                    <SelectItem value="ph_override">PH Override</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDisabled}
                    onChange={(e) => setShowDisabled(e.target.checked)}
                    data-testid="filter-include-disabled"
                  />
                  Show disabled
                </label>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Holiday</th>
                    <th className="text-left px-3 py-2 font-medium">Country</th>
                    <th className="text-left px-3 py-2 font-medium">Prov/State</th>
                    <th className="text-left px-3 py-2 font-medium">Source</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-right px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">
                      <Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Loading…
                    </td></tr>
                  ) : holidays.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400" data-testid="holidays-empty">
                      No holidays match those filters.
                    </td></tr>
                  ) : (
                    holidays.map((h) => (
                      <tr key={h.id} className={`border-t ${h.is_disabled ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`holiday-row-${h.id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{h.holiday_date}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{h.holiday_name}</div>
                          {h.english_name && h.english_name !== h.holiday_name && (
                            <div className="text-xs text-slate-500">{h.english_name}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{h.country_code}</td>
                        <td className="px-3 py-2 text-slate-600">{h.province_state_code || '—'}</td>
                        <td className="px-3 py-2"><SourceBadge source={h.source} /></td>
                        <td className="px-3 py-2">
                          {h.is_disabled ? (
                            <Badge variant="outline" className="text-rose-600 border-rose-200">Disabled</Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-200">Active</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleDisabled(h)}
                            data-testid={`toggle-disabled-${h.id}`}
                          >
                            {h.is_disabled ? 'Enable' : 'Disable'}
                          </Button>
                          {h.is_custom_holiday && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-600 hover:text-rose-700"
                              onClick={() => handleDelete(h)}
                              data-testid={`delete-${h.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SYNC DIAGNOSTICS TAB */}
          <TabsContent value="diagnostics" className="space-y-4 mt-4">
            <CoverageAndMetricsPanel />
            <div className="rounded-lg border p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-slate-500" />
                  <h3 className="font-medium">Per-country status</h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="diagnostics-running-state">
                  <StatusDot status={diagnostics?.currently_syncing ? 'running' : 'success'} />
                  {diagnostics?.currently_syncing
                    ? `Syncing ${diagnostics.current_scope || '...'}`
                    : 'Idle'}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-slate-500">
                  <tr>
                    <th className="text-left py-1.5 font-medium">Country</th>
                    <th className="text-left py-1.5 font-medium">Last status</th>
                    <th className="text-left py-1.5 font-medium">Last success</th>
                    <th className="text-left py-1.5 font-medium">Duration</th>
                    <th className="text-left py-1.5 font-medium">Imported</th>
                    <th className="text-left py-1.5 font-medium">Failed (50)</th>
                    <th className="text-left py-1.5 font-medium">Retries (50)</th>
                    <th className="text-right py-1.5 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(diagnostics?.items || []).map((d) => (
                    <tr key={d.country_code} className="border-t" data-testid={`diag-row-${d.country_code}`}>
                      <td className="py-2 font-mono text-xs">{d.country_code}</td>
                      <td className="py-2 flex items-center gap-1.5">
                        <StatusDot status={d.last_status} />
                        <span className="capitalize text-slate-600">{d.last_status || '—'}</span>
                      </td>
                      <td className="py-2 text-slate-500 text-xs">
                        {d.last_success_at ? new Date(d.last_success_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 text-slate-500 text-xs">{d.last_duration_ms != null ? `${d.last_duration_ms} ms` : '—'}</td>
                      <td className="py-2 text-slate-700">{d.last_imported_count ?? '—'}</td>
                      <td className="py-2">
                        {d.failed_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-rose-600 text-xs">
                            <AlertTriangle className="h-3 w-3" /> {d.failed_count}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">0</span>
                        )}
                      </td>
                      <td className="py-2 text-slate-500 text-xs">{d.retry_count || 0}</td>
                      <td className="py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleResyncCountry(d.country_code)}
                          disabled={diagnostics?.currently_syncing}
                          data-testid={`diag-resync-${d.country_code}`}
                        >
                          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resync
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recent logs */}
            <div className="rounded-lg border p-4 bg-white">
              <h3 className="font-medium mb-3">Recent sync logs</h3>
              {syncLogs.length === 0 ? (
                <p className="text-sm text-slate-400">No sync history yet.</p>
              ) : (
                <ol className="space-y-1.5 text-xs font-mono max-h-72 overflow-auto">
                  {syncLogs.map((l) => (
                    <li key={l.id} className="flex items-center gap-2" data-testid={`sync-log-${l.id}`}>
                      <StatusDot status={l.status} />
                      <span className="text-slate-400 w-44 shrink-0">{new Date(l.started_at).toLocaleString()}</span>
                      <span className="w-10 font-semibold">{l.country_code}</span>
                      <span className="w-12 text-slate-500">{l.year}</span>
                      <span className="capitalize text-slate-600 w-20">{l.status}</span>
                      <span className="text-slate-500">{l.duration_ms}ms · {l.imported_count} rows</span>
                      {l.errors && <span className="text-rose-600 truncate ml-2">{l.errors}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </TabsContent>

          {/* BULK & PACKS TAB */}
          <TabsContent value="bulk" className="space-y-4 mt-4">
            <BulkAndPacksPanel />
          </TabsContent>

          {/* IMPACT TAB */}
          <TabsContent value="impact" className="space-y-4 mt-4">
            <UpcomingImpactPanel />
          </TabsContent>

          {/* FEATURE FLAGS TAB */}
          <TabsContent value="flags" className="space-y-4 mt-4">
            <FeatureFlagsPanel />
          </TabsContent>

          {/* PH OVERRIDES TAB */}
          {isPHTenant && (
            <TabsContent value="ph" className="space-y-4 mt-4">
              <div className="rounded-lg border bg-rose-50/50 p-4">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-rose-900">Philippines emergency override</h3>
                    <p className="text-sm text-rose-700/80 mt-1">
                      Use this when the Philippine government issues a mid-year proclamation (typhoon, public event) before Nager.Date has it.
                      Overrides are tenant-scoped and never affect non-PH tenants.
                    </p>
                  </div>
                  <Button size="sm" onClick={() => setShowPHModal(true)} data-testid="ph-add-override-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Add PH Override
                  </Button>
                </div>
              </div>
              <div className="rounded-lg border bg-white p-4">
                <h4 className="font-medium text-sm mb-2">Active PH overrides</h4>
                {holidays.filter((h) => h.source === 'ph_override').length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="ph-overrides-empty">No PH overrides yet.</p>
                ) : (
                  <ul className="divide-y">
                    {holidays.filter((h) => h.source === 'ph_override').map((h) => (
                      <li key={h.id} className="py-2 flex items-center justify-between" data-testid={`ph-override-${h.id}`}>
                        <div>
                          <div className="text-sm font-medium">{h.holiday_name}</div>
                          <div className="text-xs text-slate-500 font-mono">{h.holiday_date} · {h.ph_type || 'special_non_working'}</div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(h)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <PHAuditTrailPanel />
            </TabsContent>
          )}
        </Tabs>

        {/* Add custom holiday modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Holiday</DialogTitle>
              <DialogDescription>
                Tenant-scoped custom holiday. Visible only to your business.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitAdd} className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={addForm.holiday_name}
                  onChange={(e) => setAddForm((f) => ({ ...f, holiday_name: e.target.value }))}
                  data-testid="add-holiday-name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={addForm.holiday_date}
                    onChange={(e) => setAddForm((f) => ({ ...f, holiday_date: e.target.value }))}
                    data-testid="add-holiday-date"
                    required
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Select
                    value={addForm.country_code}
                    onValueChange={(v) => setAddForm((f) => ({ ...f, country_code: v }))}
                  >
                    <SelectTrigger data-testid="add-holiday-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Province/State (optional)</Label>
                <Input
                  placeholder="e.g. ON, CA-ON"
                  value={addForm.province_state_code}
                  onChange={(e) => setAddForm((f) => ({ ...f, province_state_code: e.target.value }))}
                  data-testid="add-holiday-province"
                />
                <p className="text-xs text-slate-400 mt-1">Manual entry only — auto-detect from address coming later.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={addForm.is_paid}
                  onChange={(e) => setAddForm((f) => ({ ...f, is_paid: e.target.checked }))}
                  data-testid="add-holiday-paid"
                />
                Paid holiday
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
                <Button type="submit" disabled={adding} data-testid="add-holiday-submit">
                  {adding && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  Add holiday
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* PH Override modal */}
        <Dialog open={showPHModal} onOpenChange={setShowPHModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-rose-600" /> Philippines Emergency Override
              </DialogTitle>
              <DialogDescription>
                Only available to PH tenants. Backend enforces this — non-PH tenants receive 403.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitPH} className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={phForm.holiday_name}
                  onChange={(e) => setPHForm((f) => ({ ...f, holiday_name: e.target.value }))}
                  data-testid="ph-holiday-name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={phForm.holiday_date}
                    onChange={(e) => setPHForm((f) => ({ ...f, holiday_date: e.target.value }))}
                    data-testid="ph-holiday-date"
                    required
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={phForm.ph_type}
                    onValueChange={(v) => setPHForm((f) => ({ ...f, ph_type: v }))}
                  >
                    <SelectTrigger data-testid="ph-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PH_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Note (internal)</Label>
                <Input
                  placeholder="e.g. Proclamation No. 451"
                  value={phForm.note}
                  onChange={(e) => setPHForm((f) => ({ ...f, note: e.target.value }))}
                  data-testid="ph-note"
                />
              </div>
              <div>
                <Label>
                  Reason <span className="text-rose-500">*</span>
                  <span className="text-xs text-slate-400 ml-2">(min 10 chars — compliance audit)</span>
                </Label>
                <textarea
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                  rows={2}
                  placeholder="Why is this override being added? Cite the source/proclamation."
                  value={phForm.reason}
                  onChange={(e) => setPHForm((f) => ({ ...f, reason: e.target.value }))}
                  data-testid="ph-reason"
                  required
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={phForm.is_paid}
                  onChange={(e) => setPHForm((f) => ({ ...f, is_paid: e.target.checked }))}
                />
                Paid holiday
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPHModal(false)}>Cancel</Button>
                <Button type="submit" disabled={submittingPH} data-testid="ph-submit">
                  {submittingPH && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  Save override
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
